/**
 * Permisos de personal (no administrador completo).
 * Los IDs se guardan en Firestore users/{uid}.allowedFeatures y en localStorage tras el login.
 */

export const STAFF_FEATURE_GROUPS = [
  {
    title: 'Ventas',
    items: [
      { id: 'localSales', label: 'Ventas en local', description: 'Facturación en /local' }
    ]
  },
  {
    title: 'Herramientas (mismos enlaces del panel)',
    items: [
      { id: 'stock', label: 'Gestión de stock', description: '/gestion-stock' },
      { id: 'customers', label: 'Clientes', description: '/admin/clientes' },
      { id: 'suppliers', label: 'Proveedores', description: '/admin/proveedores' },
      { id: 'subscribers', label: 'Suscriptores', description: '/subscriptores' },
      { id: 'massEditor', label: 'Editor masivo', description: '/editor-masivo' },
      { id: 'discounts', label: 'Descuentos', description: '/configuracion-descuentos' },
      { id: 'shippingCosts', label: 'Costos de envío', description: '/costo-envios' },
      { id: 'salesHistory', label: 'Historial de ventas', description: '/historial-ventas' },
      { id: 'analytics', label: 'Analíticas', description: '/analiticas' },
      { id: 'balance', label: 'Balance', description: '/balance' },
      { id: 'newsletter', label: 'Newsletter', description: '/newsletter' },
      { id: 'expenses', label: 'Gastos y cheques', description: '/admin/gastos' },
      { id: 'factory', label: 'Gestión de producción', description: '/factory' }
    ]
  },
  {
    title: 'Ventas online (pestañas dentro del panel)',
    items: [
      { id: 'adminMercadoPago', label: 'Mercado Pago admin', description: 'Pestaña en el panel' },
      { id: 'adminOrders', label: 'Pedidos recibidos', description: 'Pestaña en el panel' },
      { id: 'adminSalesMp', label: 'Ventas Mercado Pago', description: 'Pestaña en el panel' },
      { id: 'adminMessages', label: 'Mensajes de contacto', description: 'Pestaña en el panel' }
    ]
  }
];

/** Todos los IDs válidos (para validación en cliente) */
export const ALL_STAFF_FEATURE_IDS = STAFF_FEATURE_GROUPS.flatMap((g) => g.items.map((i) => i.id));

/** Vista del AdminDashboard ?view= → permiso */
export const ADMIN_VIEW_TO_FEATURE = {
  mercadopago: 'adminMercadoPago',
  orders: 'adminOrders',
  sales: 'adminSalesMp',
  messages: 'adminMessages'
};

/** Ruta de la app → permiso requerido (excepto /admin, que usa canAccessAdminShell) */
export const ROUTE_PATH_TO_FEATURE = {
  '/local': 'localSales',
  '/subscriptores': 'subscribers',
  '/editor-masivo': 'massEditor',
  '/historial-ventas': 'salesHistory',
  '/analiticas': 'analytics',
  '/configuracion-descuentos': 'discounts',
  '/costo-envios': 'shippingCosts',
  '/gestion-stock': 'stock',
  '/admin/clientes': 'customers',
  '/admin/proveedores': 'suppliers',
  '/balance': 'balance',
  '/newsletter': 'newsletter',
  '/admin/gastos': 'expenses',
  '/factory': 'factory'
};

const ONLINE_FEATURE_IDS = ['adminMercadoPago', 'adminOrders', 'adminSalesMp', 'adminMessages'];

/** Herramientas del grid (sin panel ni local, sin pestañas online) */
const TOOL_FEATURE_IDS = STAFF_FEATURE_GROUPS[1].items.map((i) => i.id);

export function normalizeStaffFromFirestore(userData) {
  const rawRole = userData?.role;
  const role =
    typeof rawRole === 'string'
      ? rawRole.trim().toLowerCase()
      : rawRole != null
        ? String(rawRole).trim().toLowerCase()
        : '';
  const features = Array.isArray(userData?.allowedFeatures)
    ? userData.allowedFeatures.filter((x) => typeof x === 'string' && x !== 'dashboard' && x !== 'invoice')
    : [];
  return { role, features };
}

export function pickPostLoginPath(isFullAdmin, featureIds) {
  if (isFullAdmin) return '/admin';
  const set = new Set(featureIds);
  if (set.size === 0) return '/login';
  const order = [
    { path: '/admin', check: () => ONLINE_FEATURE_IDS.some((id) => set.has(id)) || TOOL_FEATURE_IDS.some((id) => set.has(id)) },
    { path: '/local', check: () => set.has('localSales') },
    { path: '/gestion-stock', check: () => set.has('stock') },
    { path: '/admin/clientes', check: () => set.has('customers') },
    { path: '/admin/proveedores', check: () => set.has('suppliers') },
    { path: '/subscriptores', check: () => set.has('subscribers') },
    { path: '/editor-masivo', check: () => set.has('massEditor') },
    { path: '/historial-ventas', check: () => set.has('salesHistory') },
    { path: '/analiticas', check: () => set.has('analytics') },
    { path: '/configuracion-descuentos', check: () => set.has('discounts') },
    { path: '/costo-envios', check: () => set.has('shippingCosts') },
    { path: '/balance', check: () => set.has('balance') },
    { path: '/newsletter', check: () => set.has('newsletter') },
    { path: '/admin/gastos', check: () => set.has('expenses') },
    { path: '/factory', check: () => set.has('factory') }
  ];
  for (const { path, check } of order) {
    if (check()) return path;
  }
  return '/';
}

export function canAccessAdminShellFromFeatures(isFullAdmin, featureIds) {
  if (isFullAdmin) return true;
  const set = new Set(featureIds);
  return (
    ONLINE_FEATURE_IDS.some((id) => set.has(id)) ||
    TOOL_FEATURE_IDS.some((id) => set.has(id))
  );
}

export function showToolsGridFromFeatures(isFullAdmin, featureIds) {
  if (isFullAdmin) return true;
  const set = new Set(featureIds);
  return TOOL_FEATURE_IDS.some((id) => set.has(id));
}

export function showOnlineSectionFromFeatures(isFullAdmin, featureIds) {
  if (isFullAdmin) return true;
  const set = new Set(featureIds);
  return ONLINE_FEATURE_IDS.some((id) => set.has(id));
}

export function firstAllowedAdminSubview(featureIds) {
  const set = new Set(featureIds);
  const order = [
    ['orders', 'adminOrders'],
    ['mercadopago', 'adminMercadoPago'],
    ['sales', 'adminSalesMp'],
    ['messages', 'adminMessages']
  ];
  for (const [view, fid] of order) {
    if (set.has(fid)) return view;
  }
  return '';
}
